/**
 * The guided intake, as a graph of questions rather than a form.
 *
 * Each node asks one thing, explains in plain English why it matters, can be
 * skipped, and only appears when earlier answers make it relevant — a startup
 * is asked about projections and its own money going in; someone buying a
 * business is asked about the purchase price and the seller's financials.
 *
 * Nodes never decide eligibility. They collect facts and, where useful, add a
 * short note pointing at something to ask a lender.
 */

import type { AgeBand, DocKind, Goal, Stage, Workspace, YesNoUnsure } from '../workspace/schema.js';
import { DOC_KIND_LABELS, fact } from '../workspace/schema.js';
import { fmtMoney } from './parseAnswer.js';

export type AnswerType = 'choice' | 'multi' | 'amount' | 'count' | 'text' | 'industry';

export interface Option { value: string; label: string; hint?: string }

export type Answer =
  | { kind: 'choice'; value: string }
  | { kind: 'multi'; values: string[] }
  | { kind: 'number'; value: number; approximate?: boolean; note?: string }
  | { kind: 'text'; value: string };

export interface IntakeNode {
  id: string;
  prompt: (ws: Workspace) => string;
  /** Why this matters — shown under the question. */
  why: string;
  type: AnswerType;
  options?: (ws: Workspace) => Option[];
  placeholder?: string;
  /** Monthly amounts are entered monthly but some are stored annually. */
  unit?: 'monthly' | 'annual' | 'once';
  /** Glossary ids to offer next to the question. */
  terms?: string[];
  /** Whether the node applies, given earlier answers. Default: always. */
  when?: (ws: Workspace) => boolean;
  apply: (ws: Workspace, a: Answer) => Workspace;
  /** How the answer reads back in the conversation. */
  echo: (a: Answer, ws: Workspace) => string;
  /** Optional note from the assistant after the answer. */
  after?: (ws: Workspace, a: Answer) => string | null;
}

// ------------------------------------------------------------------ helpers

const goal = (ws: Workspace): Goal | undefined => ws.profile.goal?.value;
const stage = (ws: Workspace): Stage | undefined => ws.profile.stage?.value;
const isAcquiring = (ws: Workspace) => goal(ws) === 'buy-business' || stage(ws) === 'acquiring';
const isPlanning = (ws: Workspace) => stage(ws) === 'planning';

function setProfile(ws: Workspace, patch: Partial<Workspace['profile']>): Workspace {
  return { ...ws, profile: { ...ws.profile, ...patch } };
}

const choiceValue = (a: Answer) => (a.kind === 'choice' ? a.value : '');
const numValue = (a: Answer) => (a.kind === 'number' ? a.value : 0);

function labelOf(options: Option[], value: string): string {
  return options.find((o) => o.value === value)?.label ?? value;
}

const YES_NO: Option[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'unsure', label: "I'm not sure" },
];

export const GOAL_OPTIONS: Option[] = [
  { value: 'working-capital', label: 'Working capital or cash flow', hint: 'Payroll, inventory, smoothing out slow months' },
  { value: 'equipment-expansion', label: 'Equipment, renovation or expansion' },
  { value: 'buy-business', label: 'Buying a business' },
  { value: 'real-estate', label: 'Commercial real estate' },
  { value: 'refinance', label: 'Refinancing existing business debt' },
  { value: 'not-sure', label: "I'm not sure yet" },
];

export const STAGE_OPTIONS: Option[] = [
  { value: 'planning', label: 'Planning or startup', hint: 'Not open yet, or the loan will open it' },
  { value: 'operating', label: 'Operating business' },
  { value: 'acquiring', label: 'Acquiring an existing business' },
];

export const AGE_OPTIONS: Option[] = [
  { value: 'under-1', label: 'Under 1 year' },
  { value: '1-2', label: '1 to 2 years' },
  { value: '2-5', label: '2 to 5 years' },
  { value: 'over-5', label: 'Over 5 years' },
];

/** NAICS sectors, merged where NAICS splits one sector across codes. */
export const INDUSTRY_OPTIONS: Option[] = [
  { value: '72', label: 'Restaurants, food service and lodging' },
  { value: '44', label: 'Retail stores and e-commerce' },
  { value: '62', label: 'Health care and social assistance' },
  { value: '54', label: 'Professional, scientific and technical services' },
  { value: '23', label: 'Construction and trades' },
  { value: '81', label: 'Personal services and repair', hint: 'Salons, auto repair, dry cleaning' },
  { value: '31', label: 'Manufacturing' },
  { value: '42', label: 'Wholesale trade' },
  { value: '48', label: 'Transportation and warehousing', hint: 'Trucking, delivery, storage' },
  { value: '56', label: 'Administrative and support services', hint: 'Cleaning, staffing, landscaping' },
  { value: '53', label: 'Real estate, rental and leasing' },
  { value: '71', label: 'Arts, entertainment and recreation', hint: 'Gyms, studios, venues' },
  { value: '61', label: 'Education and training' },
  { value: '51', label: 'Information, media and software' },
  { value: '52', label: 'Finance and insurance' },
  { value: '11', label: 'Agriculture, forestry and fishing' },
  { value: '21', label: 'Mining, quarrying, oil and gas' },
  { value: '22', label: 'Utilities' },
];

export const TERM_OPTIONS: Option[] = [
  { value: 'typical', label: 'Use what is typical for my goal' },
  { value: '60', label: '5 years' },
  { value: '84', label: '7 years' },
  { value: '120', label: '10 years' },
  { value: '300', label: '25 years', hint: 'Usually for real estate' },
];

/** A reasonable starting term for each goal; the owner can change it any time. */
export const TYPICAL_TERM: Record<Goal, number> = {
  'working-capital': 84,
  'equipment-expansion': 120,
  'buy-business': 120,
  'real-estate': 300,
  refinance: 120,
  'not-sure': 120,
};

const DOC_OPTIONS: Option[] = (
  ['pl', 'tax-return', 'balance-sheet', 'bank-statement', 'business-plan'] as DocKind[]
).map((k) => ({ value: k, label: DOC_KIND_LABELS[k] }));

// -------------------------------------------------------------------- nodes

export const NODES: IntakeNode[] = [
  {
    id: 'goal',
    prompt: () => 'What are you looking to fund?',
    why: 'Different goals lead to different SBA programs, loan lengths and paperwork. You can change this later.',
    type: 'choice',
    options: () => GOAL_OPTIONS,
    terms: ['working-capital'],
    apply: (ws, a) => {
      const g = choiceValue(a) as Goal;
      let next = setProfile(ws, { goal: fact(g) });
      if (g === 'buy-business') next = setProfile(next, { stage: fact<Stage>('acquiring') });
      return next;
    },
    echo: (a) => labelOf(GOAL_OPTIONS, choiceValue(a)),
    after: (_ws, a) => {
      switch (choiceValue(a)) {
        case 'real-estate':
          return 'Worth knowing: SBA has two main routes for property — 7(a) loans and 504 loans, which are built for buildings and long-life equipment. A lender can tell you which fits.';
        case 'refinance':
          return 'Refinancing has its own rules about which debts qualify, so we will gather the details of what you pay today.';
        case 'not-sure':
          return "That's fine — a couple of quick questions will help narrow it down.";
        default:
          return null;
      }
    },
  },
  {
    id: 'not-sure-helper',
    prompt: () => 'Which sounds closest to what is going on?',
    why: 'This points us at the right questions. Pick the nearest one — nothing is locked in.',
    type: 'choice',
    when: (ws) => goal(ws) === 'not-sure',
    options: () => [
      { value: 'working-capital', label: 'Cash is tight from month to month' },
      { value: 'equipment-expansion', label: 'I want to grow, renovate or buy equipment' },
      { value: 'buy-business', label: 'I want to buy a business' },
      { value: 'real-estate', label: 'I want to buy or build a property' },
      { value: 'refinance', label: 'My current loan payments are too high' },
      { value: 'still', label: 'Still not sure' },
    ],
    apply: (ws, a) => {
      const v = choiceValue(a);
      if (v === 'still') return ws;
      let next = setProfile(ws, { goal: fact(v as Goal) });
      if (v === 'buy-business') next = setProfile(next, { stage: fact<Stage>('acquiring') });
      return next;
    },
    echo: (a) => (choiceValue(a) === 'still' ? 'Still not sure' : labelOf(GOAL_OPTIONS, choiceValue(a))),
    after: (_ws, a) => (choiceValue(a) === 'still'
      ? "No problem. I'll ask general questions, and the action plan will list options to talk through with a lender."
      : null),
  },
  {
    id: 'stage',
    prompt: () => 'Where is the business today?',
    why: 'A business that is already running is judged mostly on its track record; a startup is judged on its plan and the owner.',
    type: 'choice',
    when: (ws) => goal(ws) !== 'buy-business',
    options: () => STAGE_OPTIONS,
    apply: (ws, a) => {
      const s = choiceValue(a) as Stage;
      let next = setProfile(ws, { stage: fact(s) });
      if (s === 'planning') next = setProfile(next, { ageBand: fact<AgeBand>('not-operating') });
      return next;
    },
    echo: (a) => labelOf(STAGE_OPTIONS, choiceValue(a)),
  },
  {
    id: 'industry',
    prompt: (ws) => (isAcquiring(ws) ? 'What kind of business are you buying?' : 'What kind of business is it?'),
    why: 'Industries differ in how SBA loans have performed. We only use the broad sector.',
    type: 'industry',
    options: () => INDUSTRY_OPTIONS,
    apply: (ws, a) => setProfile(ws, { industry: fact(choiceValue(a)) }),
    echo: (a) => labelOf(INDUSTRY_OPTIONS, choiceValue(a)),
  },
  {
    id: 'age',
    prompt: (ws) => (isAcquiring(ws)
      ? 'How long has the business you are buying been operating?'
      : 'How long has the business been operating?'),
    why: 'Operating history is one of the clearest signals in SBA loan data. Businesses with more history have generally repaid more reliably.',
    type: 'choice',
    when: (ws) => !isPlanning(ws),
    options: () => AGE_OPTIONS,
    apply: (ws, a) => setProfile(ws, { ageBand: fact(choiceValue(a) as AgeBand) }),
    echo: (a) => labelOf(AGE_OPTIONS, choiceValue(a)),
  },
  {
    id: 'amount',
    prompt: () => 'About how much funding do you need?',
    why: 'The amount shapes which SBA programs fit, the maximum rate a lender may charge, and your payment. A rough figure is fine.',
    type: 'amount',
    unit: 'once',
    placeholder: 'e.g. 150k or $1.2m',
    apply: (ws, a) => setProfile(ws, { amount: fact(numValue(a)) }),
    echo: (a) => fmtMoney(numValue(a)),
    after: (_ws, a) => (numValue(a) > 5_000_000
      ? 'That is above the standard SBA 7(a) maximum of $5 million, so part of it would need another source. The action plan will flag this.'
      : null),
  },
  {
    id: 'purchase-price',
    prompt: (ws) => (goal(ws) === 'real-estate' ? 'What is the price of the property?' : 'What is the purchase price of the business?'),
    why: 'Lenders compare the loan to the total price to see how much you are putting in.',
    type: 'amount',
    unit: 'once',
    when: (ws) => goal(ws) === 'buy-business' || goal(ws) === 'real-estate',
    apply: (ws, a) => setProfile(ws, { purchasePrice: fact(numValue(a)) }),
    echo: (a) => fmtMoney(numValue(a)),
  },
  {
    id: 'owner-injection',
    prompt: () => 'How much of your own money are you putting in?',
    why: 'Lenders usually expect owners to share the risk with their own money, especially for startups and purchases.',
    type: 'amount',
    unit: 'once',
    terms: ['owner-injection'],
    when: (ws) => isPlanning(ws) || goal(ws) === 'buy-business' || goal(ws) === 'real-estate',
    apply: (ws, a) => setProfile(ws, { ownerInjection: fact(numValue(a)) }),
    echo: (a) => fmtMoney(numValue(a)),
  },
  {
    id: 'funds-description',
    prompt: () => 'In a sentence, what will the money pay for?',
    why: 'Lenders ask for a clear use of funds. Writing it down now goes straight into your action plan.',
    type: 'text',
    placeholder: 'e.g. A second oven and a delivery van',
    when: (ws) => ['working-capital', 'equipment-expansion', 'not-sure'].includes(goal(ws) ?? ''),
    apply: (ws, a) => setProfile(ws, { fundsDescription: fact(a.kind === 'text' ? a.value : '') }),
    echo: (a) => (a.kind === 'text' ? a.value : ''),
  },
  {
    id: 'seller-financials',
    prompt: () => "Can you get the seller's financial statements and tax returns for the last few years?",
    why: "When you buy a business, the lender judges it largely on the seller's records.",
    type: 'choice',
    when: (ws) => goal(ws) === 'buy-business',
    options: () => YES_NO,
    apply: (ws, a) => setProfile(ws, { sellerFinancialsAvailable: fact(choiceValue(a) as YesNoUnsure) }),
    echo: (a) => labelOf(YES_NO, choiceValue(a)),
  },
  {
    id: 'occupancy',
    prompt: () => 'Will your own business use most of the property?',
    why: 'Lenders will ask how much of the property your business occupies; it affects which programs fit.',
    type: 'choice',
    when: (ws) => goal(ws) === 'real-estate',
    options: () => YES_NO,
    apply: (ws, a) => setProfile(ws, { occupancyMajority: fact(choiceValue(a) as YesNoUnsure) }),
    echo: (a) => labelOf(YES_NO, choiceValue(a)),
  },
  {
    id: 'refinance-payment',
    prompt: () => 'What do you pay each month on the debt you want to refinance?',
    why: 'This shows how much a new loan could change your monthly costs.',
    type: 'amount',
    unit: 'monthly',
    when: (ws) => goal(ws) === 'refinance',
    apply: (ws, a) => setProfile(ws, { refinanceMonthlyPayment: fact(numValue(a)) }),
    echo: (a) => `${fmtMoney(numValue(a))} a month`,
  },
  {
    id: 'projections',
    prompt: () => 'Do you have a business plan with financial projections?',
    why: 'Without a track record, the plan and projections are what a lender evaluates.',
    type: 'choice',
    when: isPlanning,
    options: () => YES_NO,
    apply: (ws, a) => setProfile(ws, { hasProjections: fact(choiceValue(a) as YesNoUnsure) }),
    echo: (a) => labelOf(YES_NO, choiceValue(a)),
  },
  {
    id: 'revenue',
    prompt: (ws) => (isAcquiring(ws)
      ? 'Roughly what were the sales of the business you are buying over the last 12 months?'
      : 'Roughly what were your sales over the last 12 months?'),
    why: 'Sales and profit show whether the business can carry a new payment. A rough figure is fine — you can confirm it from documents later.',
    type: 'amount',
    unit: 'annual',
    when: (ws) => !isPlanning(ws),
    apply: (ws, a) => setProfile(ws, { annualRevenue: fact(numValue(a)) }),
    echo: (a) => `${fmtMoney(numValue(a))} a year`,
  },
  {
    id: 'operating-income',
    prompt: () => 'About how much did it earn in that time before loan payments, interest and taxes?',
    why: 'This is the money available to make loan payments. Lenders compare it with your total loan payments.',
    type: 'amount',
    unit: 'annual',
    terms: ['operating-income', 'dscr'],
    placeholder: 'e.g. 85k, or "lost 10k"',
    when: (ws) => !isPlanning(ws),
    apply: (ws, a) => setProfile(ws, { operatingIncome: fact(numValue(a)) }),
    echo: (a) => `${fmtMoney(numValue(a))} a year`,
  },
  {
    id: 'existing-debt',
    prompt: () => 'How much does the business pay each month on existing loans? Enter 0 if none.',
    why: 'A new payment adds to what you already pay. Lenders look at the total.',
    type: 'amount',
    unit: 'monthly',
    terms: ['debt-service'],
    when: (ws) => stage(ws) === 'operating',
    apply: (ws, a) => setProfile(ws, { existingAnnualDebtService: fact(numValue(a) * 12) }),
    echo: (a) => (numValue(a) === 0 ? 'None' : `${fmtMoney(numValue(a))} a month`),
  },
  {
    id: 'uplift',
    prompt: () => 'If this works as planned, about how much extra profit per month do you expect it to bring in?',
    why: 'This lets us show whether the loan could pay for itself. Your own estimate — we never invent it.',
    type: 'amount',
    unit: 'monthly',
    when: (ws) => ['equipment-expansion', 'working-capital', 'real-estate'].includes(goal(ws) ?? '') && !isPlanning(ws),
    apply: (ws, a) => setProfile(ws, { expectedMonthlyUplift: fact(numValue(a)) }),
    echo: (a) => `${fmtMoney(numValue(a))} a month`,
  },
  {
    id: 'jobs',
    prompt: () => 'How many jobs will this funding help create or keep, including yours?',
    why: 'SBA records jobs supported for every loan; it is a small factor in the estimate.',
    type: 'count',
    placeholder: 'e.g. 6',
    apply: (ws, a) => setProfile(ws, { jobs: fact(Math.max(0, Math.round(numValue(a)))) }),
    echo: (a) => `${Math.round(numValue(a))}`,
  },
  {
    id: 'term',
    prompt: () => 'Do you have a repayment length in mind?',
    why: 'A longer term means smaller payments but more interest overall. You can compare options in Scenarios.',
    type: 'choice',
    options: () => TERM_OPTIONS,
    apply: (ws, a) => {
      const v = choiceValue(a);
      const months = v === 'typical' ? TYPICAL_TERM[goal(ws) ?? 'not-sure'] : Number(v);
      return setProfile(ws, { termMonths: fact(months) });
    },
    echo: (a, ws) => (choiceValue(a) === 'typical'
      ? `Typical for my goal (${TYPICAL_TERM[goal(ws) ?? 'not-sure'] / 12} years)`
      : labelOf(TERM_OPTIONS, choiceValue(a))),
  },
  {
    id: 'docs-on-hand',
    prompt: () => 'Which of these documents do you already have?',
    why: 'This builds your personal checklist. You can upload them next, and figures will be read on your device.',
    type: 'multi',
    options: () => DOC_OPTIONS,
    apply: (ws, a) => ({ ...ws, docsOnHand: (a.kind === 'multi' ? a.values : []) as DocKind[] }),
    echo: (a) => {
      const v = a.kind === 'multi' ? a.values : [];
      return v.length === 0 ? 'None of these yet' : v.map((k) => labelOf(DOC_OPTIONS, k)).join(', ');
    },
  },
];

export const NODE_BY_ID = new Map(NODES.map((n) => [n.id, n]));
